import campaigns from 'src/config/campaigns.json'

/**
 * @public Consumido em src/sdk/product/useProductLinkV2.ts via
 * `import('src/utils/shippingCampaign').ShippingBadgeKey` (type-only
 * dynamic import), um padrão que o analisador estático do knip não
 * rastreia - por isso ele reporta este export como não usado mesmo
 * havendo um consumidor real. SPDBR-5490 T5.29.
 */
export type ShippingBadgeKey =
  | 'frete_gratis_sudeste'
  | 'frete_gratis_consulte'
  | 'frete_gratis_todas_regioes'

type ShippingCampaignRegion =
  | 'sudeste'
  | 'sul-centro-oeste'
  | 'nordeste'
  | 'norte'

const STATE_TO_REGION: Record<string, ShippingCampaignRegion> = {
  SP: 'sudeste',
  RJ: 'sudeste',
  MG: 'sudeste',
  ES: 'sudeste',
  RS: 'sul-centro-oeste',
  SC: 'sul-centro-oeste',
  PR: 'sul-centro-oeste',
  GO: 'sul-centro-oeste',
  MT: 'sul-centro-oeste',
  MS: 'sul-centro-oeste',
  DF: 'sul-centro-oeste',
  AL: 'nordeste',
  BA: 'nordeste',
  CE: 'nordeste',
  MA: 'nordeste',
  PB: 'nordeste',
  PE: 'nordeste',
  PI: 'nordeste',
  RN: 'nordeste',
  SE: 'nordeste',
  AC: 'norte',
  AM: 'norte',
  AP: 'norte',
  PA: 'norte',
  RO: 'norte',
  RR: 'norte',
  TO: 'norte',
}

const REGION_MIN_PRICE_CENTS: Record<ShippingCampaignRegion, number> = {
  sudeste: 27999,
  'sul-centro-oeste': 32999,
  nordeste: 32999,
  norte: 42999,
}

export function getRegionFromState(
  state: string
): ShippingCampaignRegion | null {
  const normalizedState = state.trim().toUpperCase()

  return STATE_TO_REGION[normalizedState] ?? null
}

function getMinPriceCentsForRegion(region: ShippingCampaignRegion): number {
  return REGION_MIN_PRICE_CENTS[region]
}

export type ShippingCampaignEvaluation = {
  isEligible: boolean
  badgeKey?: ShippingBadgeKey
  badgeText?: string
  badgeSubtext?: string
  analyticsBadgeKey?: ShippingBadgeKey
  ctaLabel: string
  ctaActionText: string
  modal: ShippingCampaignModal
}

type ShippingCampaignModalRule = {
  id: string
  regionLabel: string
  connectorText: string
  priceLabel: string
}

type ShippingCampaignModal = {
  title: string
  subtitle: string
  rules: ShippingCampaignModalRule[]
  footer: string
}

type ShippingRange = {
  minCents: number
  maxCents?: number
  badgeKey: ShippingBadgeKey
  displayText: string
  badgeSubtext?: string
}

const shippingCampaign = campaigns.shippingFreeCampaign

function mapLegacyRule(rule: string, index: number): ShippingCampaignModalRule {
  const [regionLabel, details = ''] = rule.split(' — ')
  const priceMatch = details.match(/R\$\s?[\d.,]+/)
  const [priceLabel] = priceMatch ?? ['']

  return {
    id: `legacy-rule-${index}`,
    regionLabel,
    connectorText: details.replace(priceLabel, '').trim(),
    priceLabel,
  }
}

function normalizeModal(modal: {
  title: string
  subtitle: string
  rules?: string[]
  rulesStructured?: ShippingCampaignModalRule[]
  footer: string
}): ShippingCampaignModal {
  const rules =
    modal.rulesStructured && modal.rulesStructured.length > 0
      ? modal.rulesStructured
      : (modal.rules ?? []).map(mapLegacyRule)

  return {
    title: modal.title,
    subtitle: modal.subtitle,
    rules,
    footer: modal.footer,
  }
}

const normalizedShippingCampaignModal = normalizeModal(shippingCampaign.modal)

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function getPriceInCents(price?: number | null): number {
  if (typeof price !== 'number' || Number.isNaN(price)) {
    return 0
  }

  return Math.round(price * 100)
}

function isSellerEligible(sellerName?: string | null): boolean {
  if (!sellerName) {
    return false
  }

  const normalizedSellerName = normalizeText(sellerName)
  const normalizedEligibleSeller = normalizeText(
    shippingCampaign.eligibility.sellerName
  )

  return normalizedSellerName.includes(normalizedEligibleSeller)
}

function isCategoryEligible(categoryTree: string[] = []): boolean {
  if (!categoryTree.length) {
    return false
  }

  const normalizedTree = categoryTree.map((category) => normalizeText(category))

  return shippingCampaign.eligibility.categories.some((allowedCategory) =>
    normalizedTree.some((category) =>
      category.includes(normalizeText(allowedCategory))
    )
  )
}

function getPriceRange(priceCents: number): ShippingRange | undefined {
  const ranges = shippingCampaign.priceRanges as ShippingRange[]

  return ranges.find((range) => {
    const inMinRange = priceCents >= range.minCents
    const inMaxRange =
      typeof range.maxCents === 'number' ? priceCents < range.maxCents : true

    return inMinRange && inMaxRange
  })
}

function buildIneligibleEvaluation(): ShippingCampaignEvaluation {
  return {
    isEligible: false,
    ctaLabel: shippingCampaign.cardCta.ineligible.label,
    ctaActionText: shippingCampaign.cardCta.ineligible.actionText,
    modal: normalizedShippingCampaignModal,
  }
}

function buildEligibleEvaluation(
  priceRange: ShippingRange,
  badgeSubtext?: string
): ShippingCampaignEvaluation {
  return {
    isEligible: true,
    badgeKey: priceRange.badgeKey,
    badgeText: priceRange.displayText,
    badgeSubtext,
    analyticsBadgeKey: priceRange.badgeKey,
    ctaLabel: shippingCampaign.cardCta.eligible.label,
    ctaActionText: shippingCampaign.cardCta.eligible.actionText,
    modal: normalizedShippingCampaignModal,
  }
}

export function evaluateShippingCampaign(params: {
  price?: number | null
  cashPrice?: number | null
  sellerName?: string | null
  categoryTree?: string[]
  userState?: string | null
}): ShippingCampaignEvaluation {
  const { price, cashPrice, sellerName, categoryTree = [], userState } = params

  const effectivePrice =
    typeof cashPrice === 'number' && cashPrice > 0 ? cashPrice : price

  const priceCents = getPriceInCents(effectivePrice)
  const eligibleBySeller = isSellerEligible(sellerName)
  const eligibleByCategory = isCategoryEligible(categoryTree)
  const priceRange = getPriceRange(priceCents)

  if (!eligibleBySeller || !eligibleByCategory) {
    return buildIneligibleEvaluation()
  }

  const normalizedUserState = userState?.trim()

  if (normalizedUserState) {
    const region = getRegionFromState(normalizedUserState)

    if (region) {
      const regionMinPriceCents = getMinPriceCentsForRegion(region)

      if (priceCents < regionMinPriceCents || !priceRange) {
        return buildIneligibleEvaluation()
      }

      return buildEligibleEvaluation(priceRange)
    }
  }

  const hasMinPrice = priceCents >= shippingCampaign.eligibility.minPriceCents
  const isEligible = hasMinPrice && !!priceRange

  if (!isEligible || !priceRange) {
    return buildIneligibleEvaluation()
  }

  return buildEligibleEvaluation(priceRange, priceRange.badgeSubtext)
}
